import React, {
  useEffect,
  useRef,
  useState,
  Suspense,
  lazy,
  useMemo,
} from "react";
import { Chip, Grid, Stack, Tooltip, Typography } from "@mui/material";
import axios from "axios";
import Layout from "../../../layout/Layout";
import { config, positionMasterAPI } from "../../../config";
import { AddPosition } from "../../../components/AddPosition";
import NotFound from "../../../components/NotFount";
import { Create } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { LoadingButton } from "@mui/lab";
import AddIcon from "@mui/icons-material/Add";
import DataTable from "react-data-table-component";
import EditLocationOutlinedIcon from "@mui/icons-material/EditLocationOutlined";
import IconButton from "@mui/material/IconButton";
import DeleteTwoToneIcon from "@mui/icons-material/DeleteTwoTone";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { mainMenuList } from "../../../layout/Sidenav";
import { Tooltip as ReactTooltip } from "react-tooltip";
import {
  dataTableCustomStyles,
  serialNumberColumn,
} from "../../../components/dataTableStyles";

// Human-readable summary of a saved `menus` CSV, now that child ids are
// configurable too (parents that have no explicit child grant are treated as
// "whole module", matching Sidenav.jsx's own legacy-grant behavior).
const describeMenus = (menusCSV) => {
  if (!menusCSV) return "";
  const ids = new Set(menusCSV.split(",").map((id) => parseInt(id, 10)));

  return mainMenuList
    .filter((menu) => ids.has(menu.id))
    .map((menu) => {
      if (!menu.childMenuFlag || !menu.childMenu?.length) return menu.title;
      const grantedChildren = menu.childMenu.filter((c) => ids.has(c.id));
      if (grantedChildren.length === 0 || grantedChildren.length === menu.childMenu.length) {
        return menu.title; // no children listed (legacy) or every child -> whole module
      }
      return `${menu.title} (${grantedChildren.map((c) => c.title).join(", ")})`;
    })
    .join(", ");
};

function AddRoleMaster() {
  const navigate = useNavigate();

  let intOffset = 0;
  const limit = 10;
  const [totalCount, setTotalCount] = useState(0);
  const [positionMasterList, setpositionMasterList] = useState([]);

  const childRef = useRef();

  const handleEditPositions = (
    id,
    Role,
    positionName,
    department_id,
    Status,
    menus,
    can_create_invoice,
    can_edit_invoice
  ) => {
    // Jump back up to the form (Edit is clicked from the table further down
    // the page) so the admin actually sees the fields fill in.
    childRef.current.setData(
      JSON.stringify({
        intPositionID: id,
        intRoleID: Role,
        positionName: positionName,
        intDepartmentID: department_id,
        status: Status,
        intType: 3,
        intMenuID: menus,
        can_create_invoice: can_create_invoice,
        can_edit_invoice: can_edit_invoice,
      })
    );
    // The page body itself never scrolls (window.scrollY is always 0) —
    // Layout.jsx's ".topnav" content column is the actual scrollable
    // element (overflow-y: auto), so that's what needs resetting, not the
    // window. Deferred to the next frame so it runs after setData()'s
    // re-render (fields fill in, the menu tree may expand/shrink) has
    // settled, instead of racing a layout change that's still in flight.
    requestAnimationFrame(() => {
      document.querySelector(".topnav")?.scrollTo({ top: 0, behavior: "smooth" });
    });
  };

  const [loading, setLoading] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);

  let tempRefs = useRef(false);
  useEffect(() => {
    if (tempRefs.current === false) {
      fetchUsers(1);
    }
  }, []);

  const fetchUsers = async (page, rowsPerPage) => {
    if (tempRefs.current === false) {
      const data = JSON.stringify({
        intLimit: rowsPerPage ? rowsPerPage : 10,
        intOffset: rowsPerPage ? (page - 1) * rowsPerPage : 0,
        intType: 7,
      });

      config.method = "POST";
      config.data = data;
      config.url = positionMasterAPI;

      axios(config)
        .then(function (response) {
          setpositionMasterList(response.data["result"]);
          setTotalCount(response.data.totalCount);
        })
        .catch(function (error) {
          console.log(error);
        });
    }
    tempRefs.current = true;
  };

  const handlePageChange = (page) => {
    tempRefs.current = false;
    fetchUsers(page, rowsPerPage);
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = async (newRowsPerPage) => {
    if (!positionMasterList.length) return;
    tempRefs.current = false;
    fetchUsers(1, newRowsPerPage);
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1);
  };

  const columns = useMemo(() => [
    serialNumberColumn(currentPage, rowsPerPage),
    {
      name: "Department Name",
      field: "id",
      sortable: true,
      width: "12%",
      selector: (row) => row.department_name,
    },
    {
      name: "Position Name",
      field: "id",
      sortable: true,
      width: "14%",
      selector: (row) => row.position_name,
    },
    {
      name: "Role Name",
      field: "id",
      sortable: true,
      width: "14%",
      selector: (row) => row.role_name,
    },
    {
      name: "Menus",
      field: "id",
      sortable: true,
      width: "23%",
      wrap: true,
      style: {
        whiteSpace: "normal",
        wordBreak: "break-word",
        lineHeight: 1.9,
      },
      // Whole module granted (every child present, or a legacy row with no
      // child ids at all) -> just the module name. Only some children granted
      // -> module name plus which ones, e.g. "Invoice (Invoice List, Invoice
      // History)".
      selector: (row) => describeMenus(row.menus),
    },
    {
      name: "Invoice Access",
      field: "id",
      width: "13%",
      wrap: true,
      cell: (row) => {
        const hasOrders = row.menus
          ?.split(",")
          .map(Number)
          .includes(2);
        // Invoice access only applies when the Orders menu is granted.
        if (!hasOrders) {
          return (
            <Typography variant="body2" sx={{ color: "text.disabled" }}>
              N/A
            </Typography>
          );
        }
        const item = (ok, label) => (
          <Chip
            icon={
              ok ? (
                <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />
              ) : (
                <HighlightOffIcon sx={{ fontSize: 16 }} />
              )
            }
            label={label}
            size="small"
            variant="outlined"
            sx={{
              borderRadius: "6px",
              fontSize: 11,
              fontWeight: 700,
              height: 26,
              justifyContent: "flex-start",
              minWidth: 76,
              color: ok ? "#1b5e20" : "#9f1239",
              borderColor: ok ? "#81c784" : "#f8a9b6",
              backgroundColor: ok ? "#edf7ed" : "#fff1f2",
              "& .MuiChip-icon": {
                color: "inherit",
                marginLeft: "8px",
              },
              "& .MuiChip-label": {
                paddingLeft: "6px",
                paddingRight: "10px",
              },
            }}
          />
        );
        return (
          <Stack spacing={0.75} alignItems="flex-start" sx={{ py: 0.5 }}>
            {item(Number(row.can_create_invoice), "Create")}
            {item(Number(row.can_edit_invoice), "Edit")}
          </Stack>
        );
      },
    },
    {
      name: "Status",
      field: "id",
      width: "9%",
      cell: (row) => (
        <Typography
          className={`${
            row.status_name === "Active" ? `active-status` : `inactive-status`
          } `}
        >
          {row.status_name}
        </Typography>
      ),
    },
    {
      name: "Action",
      field: "id",
      width: "70px",
      center: true,
      cell: (row) => (
        <Tooltip title={`Edit position`} arrow>
          <IconButton
            color="primary"
            onClick={() =>
              handleEditPositions(
                row.id,
                row.role_id,
                row.position_name,
                row.department_id,
                row.status,
                row.menus,
                row.can_create_invoice,
                row.can_edit_invoice
              )
            }
            aria-label="add an alarm"
          >
            <EditLocationOutlinedIcon />
          </IconButton>
        </Tooltip>
      ),
    },
    // {
    //   name: "Action",
    //   field: "id",
    //   cell: (row) => (
    //     <Tooltip title={`Delete position`} arrow>
    //       <IconButton
    //         color="error"
    //         onClick={() => handleDeletedepartments(row.id)}
    //         aria-label="add an alarm"
    //       >
    //         <DeleteTwoToneIcon />
    //       </IconButton>
    //     </Tooltip>
    //   ),
    // },
  ]);

  //Fetch Data Table
  // Called by the AddPosition form after a successful add/edit so the list
  // reflects the change immediately. fetchUsers is gated by a one-shot tempRefs
  // guard, so reset it first (same pattern as handlePageChange/RowsPerPage).
  const fetchData = (action) => {
    const page = action === 1 ? 1 : currentPage;
    tempRefs.current = false;
    fetchUsers(page, rowsPerPage);
    setCurrentPage(page);
  };

  return (
    <Layout>
      <Grid className="employee-form bold-header">
        <Grid container spacing={3}>
          <Grid
            textAlign="center"
            item
            className="form-lable"
            xs={12}
            lg={6}
            md={6}
          >
            <Typography className="page_title" textAlign="left">
              Manage Roles & Positions
            </Typography>
            <Breadcrumbs aria-label="breadcrumb">
              <Link underline="hover" color="inherit" href="/dashboard">
                Dashboard
              </Link>
              <Typography color="text.primary">Position</Typography>
              <Typography color="text.primary">Add</Typography>
            </Breadcrumbs>
          </Grid>
          <Grid
            textAlign="right"
            item
            className="form-lable"
            xs={12}
            lg={6}
            md={6}
          >
            {/* <LoadingButton
              size="medium"
              variant="contained"
              color="success"
              className="button_first"
              onClick={() => navigate("/config/positions-master")}
              startIcon={<AddIcon />}
            >
              Add Position
            </LoadingButton> */}
            <LoadingButton
              size="medium"
              variant="contained"
              color="primary"
              onClick={() => navigate(-1)}
              startIcon={<ArrowBackIosIcon />}
            >
              Back
            </LoadingButton>
          </Grid>
        </Grid>
        <Grid className="form_design">
          <AddPosition ref={childRef} fetchData={fetchData} />
          <div className="empty-space-20"></div>
          <Grid className="small_table">
            <DataTable
              pagination
              paginationServer
              fixedHeader
              fixedHeaderScrollHeight="550px"
              striped
              customStyles={dataTableCustomStyles}
              columns={columns}
              data={positionMasterList}
              progressPending={loading}
              paginationTotalRows={totalCount}
              paginationDefaultPage={currentPage}
              paginationRowsPerPageOptions={[1, 10, 25, 50, 100]} //you can remove it later, just to have more pages
              paginationPerPage={rowsPerPage}
              onChangePage={handlePageChange}
              // onSort={handleSort}
              onChangeRowsPerPage={handleRowsPerPageChange}
            />
          </Grid>
        </Grid>
      </Grid>
    </Layout>
  );
}

export default AddRoleMaster;
